// PRC-046: formato de números único para toda la aplicación de Pricing.
// Español siempre: coma decimal y punto de miles también con cuatro cifras
// (27,59 €; 27,3 %; 1.234,50 €; +55,54 pp). Industrial no lo usa.

const EMPTY = '—';

const groupThousands = (integerPart: string) => integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

export interface FormatNumberOptions {
  // Decimales máximos (por defecto 2).
  decimals?: number;
  // Decimales mínimos (por defecto, los mismos que `decimals`: 27,50 €).
  minDecimals?: number;
  // Antepone «+» a los positivos (diferencias: +5,90 pp).
  signed?: boolean;
  // Texto si no hay valor (por defecto «—»).
  empty?: string;
}

/** 1234.5 → «1.234,50». */
export function formatNumber(value: number | null | undefined, options: FormatNumberOptions = {}): string {
  const { decimals = 2, signed = false, empty = EMPTY } = options;
  const minDecimals = Math.min(options.minDecimals ?? decimals, decimals);
  if (!isNumber(value)) return empty;
  const fixed = Math.abs(value).toFixed(decimals);
  let [integerPart, fraction = ''] = fixed.split('.');
  // Quita ceros finales hasta el mínimo pedido.
  while (fraction.length > minDecimals && fraction.endsWith('0')) fraction = fraction.slice(0, -1);
  const isZero = Number(fixed) === 0;
  const sign = value < 0 && !isZero ? '-' : signed && value > 0 && !isZero ? '+' : '';
  return `${sign}${groupThousands(integerPart)}${fraction ? `,${fraction}` : ''}`;
}

/**
 * Sustituto de `new Intl.NumberFormat('es-ES', opciones)` con las mismas
 * opciones (moneda, porcentaje, decimales), pero con punto de miles también en
 * cifras de cuatro dígitos (es-ES no lo pone: «1234,50 €» → «1.234,50 €»).
 */
export function pricingNumberFormat(options: Intl.NumberFormatOptions = {}) {
  const base = new Intl.NumberFormat('es-ES', { ...options, useGrouping: false });
  const format = (value: number | bigint) => base
    .formatToParts(value)
    .map((part) => (part.type === 'integer' ? groupThousands(part.value) : part.value))
    .join('');
  return { format, resolvedOptions: () => base.resolvedOptions() };
}

/** Sustituto de `valor.toLocaleString('es-ES', opciones)` para números. */
export const toSpanishNumber = (value: number | null | undefined, options: Intl.NumberFormatOptions = {}, empty = EMPTY) =>
  (isNumber(value) ? pricingNumberFormat(options).format(value) : empty);

/** Entero con punto de miles: 500000 → «500.000». */
export const formatInteger = (value: number | null | undefined, empty = EMPTY) =>
  formatNumber(value, { decimals: 0, empty });

/** Importe en euros: 1234.5 → «1.234,50 €». */
export const formatEuro = (value: number | null | undefined, decimals = 2, empty = EMPTY) =>
  (isNumber(value) ? `${formatNumber(value, { decimals })} €` : empty);

/** Porcentaje ya en tanto por cien: 27.31 → «27,3 %». */
export const formatPercent = (value: number | null | undefined, decimals = 1, options: { signed?: boolean; empty?: string } = {}) =>
  (isNumber(value) ? `${formatNumber(value, { decimals, signed: options.signed })} %` : options.empty ?? EMPTY);

/** Fracción (0,273) como porcentaje: «27,3 %». */
export const formatRatioPercent = (value: number | null | undefined, decimals = 1, options: { signed?: boolean; empty?: string } = {}) =>
  (isNumber(value) ? formatPercent(value * 100, decimals, options) : options.empty ?? EMPTY);

/** Puntos porcentuales con signo: 55.54 → «+55,54 pp». */
export const formatPp = (value: number | null | undefined, decimals = 2, empty = EMPTY) =>
  (isNumber(value) ? `${formatNumber(value, { decimals, signed: true })} pp` : empty);
